import type { OptionPathsByFilter } from '@/context/FiltersContext/FiltersContext';
import type { UserFilterMetadata } from '@/models/user/userFilter';
import type { FilterState } from '@/types/filter/filterState';
import { SharedViewScenario, type SharedViewValidationResult } from '@/hooks/useSharedViewValidation';
import type { SharedViewDialogProps } from '@/components/filter/SharedViewDialog/SharedViewDialog';
import { trackSharedViewLoadFailed } from '@/hooks/AdobeAnalyticsHooks/sharedViewAnalytics';
import { SHARE_VIEW_DEBUG_PARAM } from '@/utils/filter/shareViewParam';
import {
  buildSharedViewFailureDetails,
  type SharedViewFailureContext,
  type SharedViewFailureDetails,
} from '@/utils/filter/sharedViewDiagnostics';

export interface SharedViewDialogState {
  scenario: SharedViewScenario;
  validation: SharedViewValidationResult;
  localFiltersData?: Record<string, Record<string, unknown>>;
  filterMetadata?: UserFilterMetadata;
  optionPaths?: OptionPathsByFilter;
  senderCountryLabel?: string;
  recipientCountryLabel?: string;
  errorDetails?: SharedViewFailureDetails;
}

export const buildErrorDialogState = (
  defaultFiltersState: FilterState,
  failure: SharedViewFailureContext,
  error: unknown,
): SharedViewDialogState => ({
  scenario: SharedViewScenario.ERROR,
  validation: {
    scenario: SharedViewScenario.ERROR,
    validFilters: defaultFiltersState,
    missingFilters: [],
    senderCountryCodes: [],
    recipientCountryCodes: [],
  },
  errorDetails: buildSharedViewFailureDetails(failure, error),
});

export const buildAndTrackErrorDialogState = (
  defaultFiltersState: FilterState,
  failure: SharedViewFailureContext,
  error: unknown,
): SharedViewDialogState => {
  const dialogState = buildErrorDialogState(defaultFiltersState, failure, error);
  const { errorDetails } = dialogState;
  if (errorDetails) {
    trackSharedViewLoadFailed({
      diagnosis: errorDetails.diagnosis.kind,
      stage: errorDetails.stage,
      httpStatus: errorDetails.httpStatus,
      errorCode: errorDetails.errorCode,
      errorName: errorDetails.errorName,
    });
  }
  return dialogState;
};

export const buildSharedViewDialogProps = (
  dialogState: SharedViewDialogState,
  onApply: () => void,
  onCancel: () => void,
  searchParams: URLSearchParams,
): SharedViewDialogProps => ({
  isOpen: true,
  scenario: dialogState.scenario,
  missingFilters: dialogState.validation.missingFilters,
  senderCountryLabel: dialogState.senderCountryLabel,
  recipientCountryLabel: dialogState.recipientCountryLabel,
  errorDetails: dialogState.errorDetails,
  isDetailsEnabled: searchParams.get(SHARE_VIEW_DEBUG_PARAM) === '1',
  onApply,
  onCancel: dialogState.scenario === SharedViewScenario.ERROR ? onApply : onCancel,
});
